import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { FiEye } from "react-icons/fi";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { useSession } from "../../../../context/SessionContext";
import { filterStoreProjects } from "../../projectFilters";
import { getallWorkOrders } from "../../../../Redux/actions/Operationsactions/getworkorders";
import { getMeasurementBooks } from "./measurementBookStorage";

const MeasurementTable = () => {
  const history = useHistory();
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const { rolesdata } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [records, setRecords] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    setRecords(getMeasurementBooks());
    dispatch(getallWorkOrders(selectedProjectId || undefined));
  }, [dispatch, selectedProjectId]);

  const filteredRecords = useMemo(
    () =>
      records.filter((record) => {
        const matchesProject =
          !selectedProjectId || String(record.ProjectId) === String(selectedProjectId);
        const matchesSearch = JSON.stringify(record)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        return matchesProject && matchesSearch;
      }),
    [records, searchTerm, selectedProjectId],
  );

  const addPath = "/works/measurement-book/add";
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "MB Record No.",
        field: "RecordNumber",
        flex: 1.1,
        minWidth: 140,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Measurement Date",
        field: "MeasurementDate",
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("DD-MMM-YYYY") : "—",
      },
      {
        headerName: "Project",
        field: "ProjectName",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Work Order",
        field: "WorkOrderNumber",
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Contractor",
        field: "ContractorName",
        flex: 1.1,
        minWidth: 140,
      },
      {
        headerName: "Recorded By",
        field: "RecordedBy",
        flex: 1.1,
        minWidth: 140,
      },
      {
        headerName: "Total Qty",
        field: "TotalQuantity",
        flex: 0.8,
        minWidth: 100,
        valueFormatter: (params) => Number(params.value || 0).toLocaleString(),
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 0.9,
        minWidth: 120,
        cellRenderer: (params) => {
          const status = params.value || "Draft";
          return <StatusChip status={status} type={statusChipType(status)} />;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() =>
                history.push(addPath, {
                  ProjectId: selectedProjectId,
                })
              }
            />
          </div>
        ),
        cellRenderer: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <FiEye title="View measurement book" size="1.15em" style={{ color: "#0284c7" }} />
          </div>
        ),
      },
    ],
    [addPath, history, selectedProjectId],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (params) => params.value,
      valueFormatter: (params) =>
        params.value === null || params.value === undefined || params.value === ""
          ? "N/A"
          : params.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((column) => {
      if (column.getColDef().headerName) {
        map[column.getColId()] = column.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((previous) => ({ ...previous, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: 12, width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Measurement Book"
        >
          Measurement Book
        </div>
        <div className="d-flex align-items-center" style={{ gap: 6 }}>
          <label className="input_label m-0">Project:</label>
          <select
            className="input_styling"
            style={{ width: 220 }}
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.ProjectId} value={project.ProjectId}>
                {project.ProjectName}
              </option>
            ))}
          </select>
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search Measurement Books..."
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredRecords || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MeasurementTable;
